import { useEffect, useMemo } from 'react';
import { ISSUE_TYPE, SUMMARY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { IssueParent } from '../../../../../../../model/issue-parent/types.tsx';
import type { Card } from '../../../../../../../model/types.tsx';
import {
	type SearchResponse,
	useIssueSearch,
} from '../../../../../../../services/issue-search/index.tsx';
import { useBacklogSelector } from '../../../../../../../state/index.tsx';
import {
	getIsCompanyManaged,
	projectIdSelector,
	getProjects,
} from '../../../../../../../state/selectors/app/index.tsx';
import { issueParentsSelector } from '../../../../../../../state/selectors/issue-parent/index.tsx';
import {
	getAllPossibleProjectsForSelection,
	validateSelectedCards,
	filterIssueParentsByProjects,
} from './utils.tsx';

const SEARCH_FIELDS = [SUMMARY_TYPE, ISSUE_TYPE];

const constructSearchJql = (projectIds: number[]) =>
	`project in (${projectIds.join(
		',',
	)}) AND hierarchyLevel=1 ORDER BY lastViewed DESC, updated DESC, priority DESC`;

type IssueFields = {
	summary: string;
	issuetype: {
		id: string;
		name: string;
		iconUrl: string;
	};
};

const transformSearchResponse = (response: SearchResponse<IssueFields>): IssueParent[] =>
	response.issues.map((issue) => {
		const { id, name, iconUrl } = issue.fields.issuetype;
		return {
			id: issue.id,
			key: issue.key,
			summary: issue.fields.summary,
			issueType: {
				id,
				name,
				iconUrl,
			},
			color: undefined,
		};
	});

const EMPTY_ARRAY: IssueParent[] = [];

/**
 * Returns an array of issue parent options if the selected cards
 * are valid for a bulk parent update.
 *
 * Conducts an issue search to get the list of parents ordered as
 * per the inline field, and returns the unordered parents from
 * redux state as a fallback while this the search is in flight
 * or when it did not succeed.
 */
export const useIssueParents = (selectedCards: Card[]) => {
	const allProjects = useBacklogSelector(getProjects);
	const issueParentsInState = useBacklogSelector(issueParentsSelector);
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const projectId = useBacklogSelector(projectIdSelector);

	// Array of projects from which a valid parent selection can be made
	const applicableProjects = useMemo(
		() => (isCompanyManaged ? getAllPossibleProjectsForSelection(selectedCards, allProjects) : []),
		[allProjects, isCompanyManaged, selectedCards],
	);

	// Array of valid issue parents from state
	const filteredIssueParentsInState = useMemo(
		() =>
			isCompanyManaged
				? filterIssueParentsByProjects(issueParentsInState, applicableProjects)
				: issueParentsInState,
		[isCompanyManaged, issueParentsInState, applicableProjects],
	);

	// TMP projects do not require validation as they do not
	// include epic or subtasks cards and cross-project boards
	const isCardSelectionValid = isCompanyManaged
		? validateSelectedCards(selectedCards, allProjects)
		: true;

	const { data, loading, fetch } = useIssueSearch<IssueFields>();

	const issueParentsFromSearch = useMemo(() => data && transformSearchResponse(data), [data]);
	const issueParents = isCardSelectionValid
		? issueParentsFromSearch || filteredIssueParentsInState
		: EMPTY_ARRAY;

	useEffect(() => {
		if (isCardSelectionValid) {
			const projectIds = isCompanyManaged
				? applicableProjects.map((project) => project.id)
				: [projectId];

			fetch({
				jql: constructSearchJql(projectIds),
				fields: SEARCH_FIELDS,
			});
		}
	}, [applicableProjects, fetch, isCardSelectionValid, isCompanyManaged, projectId]);

	return { issueParents, loading, cardSelectionValid: isCardSelectionValid };
};
